import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGE_GROUPS,
  CatalogueDocument,
  DIFFICULTIES,
  LANGUAGES,
  sortSections,
  TASK_STATUSES,
  TASK_TYPES,
} from '@quizdb/common';
import { Icon } from '../common/Icon';
import { TaskListFilters } from './taskEndpoints';

/** Props for `TaskFilters` (QZ-D8). */
export interface TaskFiltersProps {
  filters: TaskListFilters;
  tags: readonly string[];
  /**
   * The whole catalogue (QZ-D107), whatever its counts; the document box and section select
   * only offer entries with approved tasks (QZ-D103, QZ-D105).
   */
  documents: readonly CatalogueDocument[];
  /** The title of `filters.jobId`'s job, if loaded (QZ-D59); the chip falls back to a short id. */
  jobTitle?: string;
  onChange(filters: TaskListFilters): void;
}

type ClearableKey = 'tag' | 'taskType' | 'difficulty' | 'ageGroup' | 'language';

/**
 * The task list's filter bar (QZ-D8, QZ-D15, QZ-D16, QZ-D47, QZ-D105, QZ-D132). Status is a
 * segmented radio group and always has a value; the other five are `form-select`s whose first
 * "any" option clears the filter. With status `approved`, a document box and section select
 * narrow further (QZ-D105). "Clear filters" appears once any of those is set.
 */
export const TaskFilters = ({ filters, tags, documents, jobTitle, onChange }: TaskFiltersProps) => {
  const { t } = useTranslation();

  const handleClearableChange =
    <K extends ClearableKey>(key: K) =>
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onChange({ ...filters, [key]: value === '' ? undefined : (value as TaskListFilters[K]) });
    };

  // QZ-D103: documents and sections with no approved task are left out of the suggestions.
  const approvedDocuments = documents.filter((catalogued) => catalogued.approved > 0);
  const selectedDocument = approvedDocuments.find(
    (catalogued) => catalogued.document === filters.document
  );
  const approvedSectionNames = selectedDocument
    ? sortSections(selectedDocument.sections.filter((section) => section.approved > 0)).map(
        (section) => section.name
      )
    : [];
  // The catalogue may still be loading, or the section's count may just have dropped to zero;
  // either way the select must still show what the list is filtered by (QZ-D105).
  const sectionOptionNames =
    filters.section !== undefined && !approvedSectionNames.includes(filters.section)
      ? [...approvedSectionNames, filters.section]
      : approvedSectionNames;

  // The URL only holds a document once it is applied, so the typed text is local state. It is
  // reset to `filters.document ?? ''` on Clear filters, a status change, and back/forward.
  //
  // Deviation from the spec text: resetting on every `filters.document` change would also fire
  // while the box is being edited down to a shorter, unmatched prefix of an applied document
  // (e.g. "Atlas" -> "Atla"), since that edit itself clears `filters.document` and would wipe
  // the box mid-keystroke. Instead, the effect below only resets when the current text disagrees
  // with `filters.document`: its exact trimmed match among approved documents (or "no match" for
  // `undefined`) must equal `filters.document`. A genuinely external change — Clear filters, a
  // status change, back/forward — always disagrees, since the text was left matching the old
  // value; an edit that caused the change agrees, since the handler already set the text itself.
  // Text still showing the previous document exactly is always reset too: that document may not
  // be among the approved ones (the catalogue still loading), so the match test alone would
  // leave it in the box after Clear filters.
  const [documentText, setDocumentText] = useState(filters.document ?? '');
  const previousDocument = useRef(filters.document);
  useEffect(() => {
    const trimmed = documentText.trim();
    const matched = approvedDocuments.find((catalogued) => catalogued.document === trimmed);
    if (trimmed === previousDocument.current || matched?.document !== filters.document) {
      setDocumentText(filters.document ?? '');
    }
    previousDocument.current = filters.document;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.document]);

  const handleDocumentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDocumentText(value);
    const match = approvedDocuments.find((catalogued) => catalogued.document === value.trim());
    if (match !== undefined) {
      if (match.document !== filters.document) {
        onChange({ ...filters, document: match.document, section: undefined });
      }
    } else if (filters.document !== undefined) {
      onChange({ ...filters, document: undefined, section: undefined });
    }
  };

  const handleDocumentBlur = () => setDocumentText(filters.document ?? '');

  const filtered =
    filters.tag !== undefined ||
    filters.taskType !== undefined ||
    filters.difficulty !== undefined ||
    filters.ageGroup !== undefined ||
    filters.language !== undefined ||
    filters.document !== undefined;

  return (
    <div className="qz-filterbar">
      <div className="qz-segmented me-2" role="radiogroup" aria-label={t('task.filter.status')}>
        {TASK_STATUSES.map((status) => (
          <span key={status}>
            <input
              type="radio"
              name="task-filter-status"
              id={`task-filter-status-${status}`}
              value={status}
              checked={filters.status === status}
              onChange={() =>
                onChange({ ...filters, status, document: undefined, section: undefined })
              }
            />
            <label htmlFor={`task-filter-status-${status}`}>{t(`task.list.tab.${status}`)}</label>
          </span>
        ))}
      </div>
      {filters.jobId && (
        <button
          type="button"
          className="qz-tag"
          onClick={() => onChange({ ...filters, jobId: undefined })}
        >
          {t('task.filter.job', { title: jobTitle ?? filters.jobId.slice(0, 8) })}
          <Icon name="xmark" />
        </button>
      )}
      <div>
        <label htmlFor="task-filter-tag" className="form-label d-block">
          {t('task.filter.tag')}
        </label>
        <select
          id="task-filter-tag"
          className="form-select form-select-sm"
          value={filters.tag ?? ''}
          onChange={handleClearableChange('tag')}
        >
          <option value="">{t('task.list.any')}</option>
          {tags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-taskType" className="form-label d-block">
          {t('task.filter.taskType')}
        </label>
        <select
          id="task-filter-taskType"
          className="form-select form-select-sm"
          value={filters.taskType ?? ''}
          onChange={handleClearableChange('taskType')}
        >
          <option value="">{t('task.list.any')}</option>
          {TASK_TYPES.map((taskType) => (
            <option key={taskType} value={taskType}>
              {t(`task.type.${taskType}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-difficulty" className="form-label d-block">
          {t('task.filter.difficulty')}
        </label>
        <select
          id="task-filter-difficulty"
          className="form-select form-select-sm"
          value={filters.difficulty ?? ''}
          onChange={handleClearableChange('difficulty')}
        >
          <option value="">{t('task.list.any')}</option>
          {DIFFICULTIES.map((difficulty) => (
            <option key={difficulty} value={difficulty}>
              {t(`task.difficulty.${difficulty}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        {/* QZ-D132: one age group filter, shown for every status. */}
        <label htmlFor="task-filter-ageGroup" className="form-label d-block">
          {t('task.filter.ageGroup')}
        </label>
        <select
          id="task-filter-ageGroup"
          className="form-select form-select-sm"
          value={filters.ageGroup ?? ''}
          onChange={handleClearableChange('ageGroup')}
        >
          <option value="">{t('task.list.any')}</option>
          {AGE_GROUPS.map((ageGroup) => (
            <option key={ageGroup} value={ageGroup}>
              {t(`task.ageGroup.${ageGroup}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-language" className="form-label d-block">
          {t('task.filter.language')}
        </label>
        <select
          id="task-filter-language"
          className="form-select form-select-sm"
          value={filters.language ?? ''}
          onChange={handleClearableChange('language')}
        >
          <option value="">{t('task.list.any')}</option>
          {LANGUAGES.map((language) => (
            <option key={language} value={language}>
              {t(`task.language.${language}`)}
            </option>
          ))}
        </select>
      </div>
      {filters.status === 'approved' && (
        <>
          <div>
            <label htmlFor="task-filter-document" className="form-label d-block">
              {t('task.filter.document')}
            </label>
            <input
              id="task-filter-document"
              type="text"
              maxLength={200}
              list="task-filter-document-options"
              className="form-control form-control-sm"
              value={documentText}
              onChange={handleDocumentChange}
              onBlur={handleDocumentBlur}
            />
            <datalist id="task-filter-document-options">
              {approvedDocuments.map((catalogued) => (
                <option key={catalogued.document} value={catalogued.document} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="task-filter-section" className="form-label d-block">
              {t('task.filter.section')}
            </label>
            <select
              id="task-filter-section"
              className="form-select form-select-sm"
              disabled={filters.document === undefined}
              value={filters.section ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                onChange({ ...filters, section: value === '' ? undefined : value });
              }}
            >
              <option value="">{t('task.filter.anySection')}</option>
              {sectionOptionNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {filtered && (
        <button
          type="button"
          className="btn btn-link btn-sm ms-auto"
          onClick={() => onChange({ status: filters.status, jobId: filters.jobId })}
        >
          <Icon name="xmark" /> {t('task.list.clearFilters')}
        </button>
      )}
    </div>
  );
};

export default TaskFilters;
